// Linking this browser as a device (docs/07 "Linked devices", "Transport, authentication"):
// enrol the two public keys — the user's first device with their one-time device code, any
// later one as pending until an active device approves it — then open a device session by
// signing the API's challenge with the ECDSA key. The session token is held in memory only; a page load signs a new
// challenge, which costs nothing and never prompts (the key is non-extractable, not
// biometric-gated as on the phone).
import { api, type Device, setSessionToken } from "../api/client";
import { unwrap } from "../api/queries";
import { base64ToBytes, bytesToBase64 } from "../proto/bytes";
import { SUITE_V1 } from "../proto/frames";
import { ulidToBytes } from "../proto/ulid";
import { AUTH_ALG, type DeviceKeys, generateDeviceKeys, signDomain } from "./keys";
import { type LinkedDevice, loadLinkedDevice, saveLinkedDevice } from "./store";

export interface Session {
  token: string;
  /** Milliseconds since the epoch. */
  expiresAt: number;
}

/** `pending` until an active device approves it; `revoked` once rejected or revoked. */
export type DeviceStatus = "pending" | "active" | "revoked";

/** Enrol this browser; `bootstrapCode` (the one-time device code) is needed only while the user has no active device. */
export async function enrolBrowser(name: string, bootstrapCode?: string): Promise<LinkedDevice> {
  const keys = await generateDeviceKeys();
  const device: Device = await unwrap(
    api.POST("/api/devices", {
      body: {
        name,
        kind: "browser",
        encryption_public_key: bytesToBase64(keys.encryptionPublicKey),
        auth_alg: AUTH_ALG,
        auth_public_key: bytesToBase64(keys.authPublicKey),
        key_suite: SUITE_V1,
        bootstrap_code: bootstrapCode || null,
      },
    }),
  );
  const linked: LinkedDevice = { deviceId: device.id, deviceIdBytes: ulidToBytes(device.id), name, keys };
  await saveLinkedDevice(linked);
  return linked;
}

/** Challenge–response with the device's auth key; the token is also what the relay `hello` carries. */
export async function openSession(device: Pick<LinkedDevice, "deviceId" | "keys">): Promise<Session> {
  const { challenge } = await unwrap(
    api.POST("/api/devices/{device_id}/challenge", { params: { path: { device_id: device.deviceId } } }),
  );
  const signature = await signDomain(device.keys.auth.privateKey, "ocs/v0/request", base64ToBytes(challenge));
  const session = await unwrap(
    api.POST("/api/devices/{device_id}/session", {
      params: { path: { device_id: device.deviceId } },
      body: { signature: bytesToBase64(signature), alg: AUTH_ALG },
    }),
  );
  setSessionToken(session.token);
  return { token: session.token, expiresAt: session.expires_at };
}

/** What the API says of this device now; `revoked` when it no longer knows it. */
export async function deviceStatus(deviceId: string): Promise<DeviceStatus> {
  const devices: Device[] = await unwrap(api.GET("/api/devices"));
  const status = devices.find((d) => d.id === deviceId)?.status;
  return status === "pending" || status === "active" ? status : "revoked";
}

/** The device this browser linked earlier, if any. */
export const restoreLinkedDevice = loadLinkedDevice;

export type { DeviceKeys, LinkedDevice };
